/**
 * Loading Skeleton Components
 * Provides visual feedback during content loading
 */
import React from 'react';

// CSS animation keyframes - add to index.css
// @keyframes shimmer {
//   0% { background-position: -200% 0; }
//   100% { background-position: 200% 0; }
// }

interface SkeletonProps {
    width?: string | number;
    height?: string | number;
    borderRadius?: string;
    style?: React.CSSProperties;
}

export const Skeleton: React.FC<SkeletonProps> = ({
    width = '100%',
    height = '1rem',
    borderRadius = '0.5rem',
    style = {}
}) => (
    <div
        style={{
            width,
            height,
            borderRadius,
            background: 'linear-gradient(90deg, rgba(255,255,255,0.05) 25%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.05) 75%)',
            backgroundSize: '200% 100%',
            animation: 'shimmer 1.5s infinite',
            ...style
        }}
    />
);

/**
 * Product Card Loading Skeleton
 */
export const ProductCardSkeleton: React.FC = () => (
    <div
        className="glass-dark"
        style={{
            borderRadius: '1rem',
            padding: '1rem',
            display: 'flex',
            gap: '1rem'
        }}
    >
        {/* Image Skeleton */}
        <Skeleton width="120px" height="120px" borderRadius="0.75rem" />

        {/* Content Skeleton */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
            <div>
                <Skeleton width="70%" height="1.25rem" style={{ marginBottom: '0.5rem' }} />
                <Skeleton width="100%" height="0.875rem" style={{ marginBottom: '0.25rem' }} />
                <Skeleton width="60%" height="0.875rem" />
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem' }}>
                <Skeleton width="80px" height="1.5rem" />
                <Skeleton width="60px" height="32px" borderRadius="8px" />
            </div>
        </div>
    </div>
);

/**
 * Category Tab Loading Skeleton
 */
export const CategoryTabsSkeleton: React.FC = () => (
    <div style={{ display: 'inline-flex', gap: '0.75rem' }}>
        {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} width="100px" height="42px" borderRadius="12px" />
        ))}
    </div>
);

/**
 * Full Menu Loading State
 */
export const MenuLoadingSkeleton: React.FC = () => (
    <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%)'
    }}>
        {/* Header Skeleton */}
        <header style={{
            padding: '1rem',
            background: 'rgba(0, 0, 0, 0.8)',
            backdropFilter: 'blur(20px)',
            borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <Skeleton width="48px" height="48px" borderRadius="50%" />
                    <div>
                        <Skeleton width="150px" height="1.25rem" style={{ marginBottom: '0.25rem' }} />
                        <Skeleton width="100px" height="0.875rem" />
                    </div>
                </div>
            </div>
        </header>

        {/* Category Tabs Skeleton */}
        <div style={{
            background: 'rgba(0, 0, 0, 0.6)',
            padding: '1rem',
            overflowX: 'auto'
        }}>
            <CategoryTabsSkeleton />
        </div>

        {/* Products Skeleton */}
        <div style={{ padding: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {[1, 2, 3, 4].map((i) => (
                <ProductCardSkeleton key={i} />
            ))}
        </div>
    </div>
);

export default Skeleton;
